import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { Card, Divider } from '@material-ui/core';
import DeploymentsPeakUsageCard from './DeploymentsPeakUsageCard';
import DeploymentsTableCard from './DeploymentsTableCard';

// Single-card wrapper that puts the per-day peak-usage chart and the
// deployments table side-by-side so they can act as a drill-down pair:
// click a bar in the chart and the table below re-renders with only
// the racks that were active on that day.
//
// Layout: one Card containing the chart (noCard), a Divider, and the
// table (also noCard). The page provides both the unfiltered
// `deployments` (so the chart shape is unaffected by the filter) and
// the pre-filtered `filteredDeployments` (for the table).
//
// `activeDay` and `onActiveDayChange` are passed through to the chart;
// the page owns this state so it can reset the filter when the user
// changes months (a stale Feb 15 selection would be meaningless on a
// March view).

DeploymentsPeakUsageAndTableCard.propTypes = {
  deployments: PropTypes.array,
  filteredDeployments: PropTypes.array,
  month: PropTypes.instanceOf(Date),
  activeDay: PropTypes.string,
  peakInstant: PropTypes.number,
  onActiveDayChange: PropTypes.func,
  partNumber: PropTypes.string,
  description: PropTypes.string,
  emptyChartMessage: PropTypes.string,
  emptyTableMessage: PropTypes.string,
  headLabel: PropTypes.array,
  showTeamColumn: PropTypes.bool,
  defaultOrderBy: PropTypes.string,
  defaultOrder: PropTypes.oneOf(['asc', 'desc'])
};

DeploymentsPeakUsageAndTableCard.defaultProps = {
  deployments: [],
  filteredDeployments: [],
  month: null,
  activeDay: null,
  peakInstant: null,
  onActiveDayChange: () => {},
  partNumber: '',
  description: '',
  emptyChartMessage: 'No deployments to chart.',
  emptyTableMessage: 'No deployments recorded.',
  headLabel: [],
  showTeamColumn: false,
  defaultOrderBy: 'createdDate',
  defaultOrder: 'desc'
};

export default function DeploymentsPeakUsageAndTableCard({
  deployments,
  filteredDeployments,
  month,
  activeDay,
  peakInstant,
  onActiveDayChange,
  partNumber,
  description,
  emptyChartMessage,
  emptyTableMessage,
  headLabel,
  showTeamColumn,
  defaultOrderBy,
  defaultOrder
}) {
  // When the user has selected a day, the table's empty-state copy
  // reflects that so they can see at a glance what the filter is
  // doing without scanning the table rows.
  const tableEmptyMessage = useMemo(() => {
    if (!activeDay) return emptyTableMessage;
    return `No racks were active on day ${activeDay.slice(8)} of ${partNumber}.`;
  }, [activeDay, emptyTableMessage, partNumber]);

  const tableDescription = useMemo(() => {
    if (!activeDay) return undefined;
    return `Racks active on day ${activeDay.slice(
      8
    )} for ${partNumber}. Concurrent active racks that are contributing to the daily peak usage are highlighted `;
  }, [activeDay, partNumber]);

  return (
    <Card data-testid="peak-usage-and-table-card">
      <DeploymentsPeakUsageCard
        deployments={deployments}
        month={month}
        activeDay={activeDay}
        onActiveDayChange={onActiveDayChange}
        description={description}
        emptyMessage={emptyChartMessage}
        noCard
      />
      <Divider />
      <DeploymentsTableCard
        headLabel={headLabel}
        deployments={filteredDeployments}
        description={tableDescription}
        emptyMessage={tableEmptyMessage}
        showTeamColumn={showTeamColumn}
        defaultOrderBy={defaultOrderBy}
        defaultOrder={defaultOrder}
        noCard
        activeDay={activeDay}
        peakInstant={peakInstant}
      />
    </Card>
  );
}
